import { type FunctionComponent, useState } from 'react';

import {
  Button,
  CustomDropdown,
  Input,
  Modal,
  type OptionT,
} from '@dr.pogodin/react-utils';

import { captureException } from '@sentry/react';

import InputWithValidation from 'components/InputWithValidation';
import Throbber from 'components/Throbber';

import useResearchLibCategories from 'hooks/researchLib/useResearchLibCategories';

import { createResearchLibLink } from 'services/researchLib';

import { updateReseachLibLink } from 'services/researchLib/updateLink';
import {
  type ResearchLibLinkT,
  getId,
  isValidUrl,
  useGlobalStateObject,
} from 'utils';

import secondaryButton from 'themes/Button/admin/secondary-v2.scss';

import dropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';

import './style.scss';
import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

type PropsT = {
  initial: ResearchLibLinkT | null;
  onClose: () => void;
};

const EditLink: FunctionComponent<PropsT> = ({
  initial,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);

  const categories = useResearchLibCategories();

  const CAT_OPTIONS: Array<OptionT<string>> = [];
  for (const cat of categories.items) {
    CAT_OPTIONS.push({
      name: cat.name,
      value: getId(cat),
    });
  }

  const [cat, setCat] = useState<string>(initial?.category ?? '');
  const [highlights, setHighlights] = useState<string>(initial?.highlights ?? '');
  const [text, setText] = useState<string>(initial?.text ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');

  const gs = useGlobalStateObject();

  return (
    <Modal onCancel={onClose}>
      <div styleName="content">
        <h1 styleName="header">Add New Link</h1>
        <CustomDropdown
          label="Category"
          onChange={(value) => {
            setCat(value as string);
          }}
          options={CAT_OPTIONS}
          theme={dropdownTheme}
          value={cat}
        />
        <Input
          label="Text"
          onChange={(e) => {
            setText(e.target.value);
          }}
          theme={inputTheme}
          value={text}
        />
        <Input
          label="Highlights"
          onChange={(e) => {
            setHighlights(e.target.value);
          }}
          theme={inputTheme}
          value={highlights}
        />
        <InputWithValidation
          label="URL"
          setValue={setUrl}
          theme={inputTheme}
          validator={isValidUrl}
          value={url}
        />
        <div styleName="buttons">
          <Button
            onClick={onClose}
            theme={secondaryButton}
          >
            Cancel
          </Button>
          <Button
            disabled={!cat || !text || !isValidUrl(url)}
            onClick={() => {
              void (async () => {
                try {
                  setBusy(true);

                  if (initial) {
                    const linkId = getId(initial);

                    const res = await updateReseachLibLink({
                      category: cat,
                      highlights,
                      id: linkId,
                      text,
                      url,
                    });

                    let list = gs.get('researchLib.links.data');
                    if (!list) throw Error('Internal error');

                    list = [...list];
                    const idx = list.findIndex((item) => item.id === linkId);
                    if (idx < 0) throw Error('Internal error');

                    list[idx] = res;
                    gs.set('researchLib.links.data', list);
                  } else {
                    const res = await createResearchLibLink({
                      category: cat,
                      highlights,
                      text,
                      url,
                    });

                    let list = gs.get('researchLib.links.data') ?? [];
                    list = [...list, res];
                    gs.set('researchLib.links.data', list);
                  }

                  onClose();
                } catch (error) {
                  const action = initial ? 'update' : 'create';

                  captureException(error);
                  // eslint-disable-next-line no-alert
                  alert(`Failed to ${action} the link`);
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            {initial ? 'Update' : 'Create'}
          </Button>
        </div>
        {busy ? <Throbber withOverlay /> : null}
      </div>
    </Modal>
  );
};

export default EditLink;
